import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import {
  Download,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  Shield,
  FileText,
  Calendar,
  User,
  Building2,
  Car,
  Printer,
  List,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Map a finding's `details.source` (the human label) → backend retry key
const SOURCE_LABEL_TO_KEY = [
  ['Vehículo y Propietario', 'runt_plate'],   // RuntPlateScraper.NAME
  ['RUNT', 'runt'],
  ['RUES', 'rues'],
  ['Policía Nacional', 'policia'],
  ['Registraduría', 'registraduria'],
  ['Procuraduría', 'procuraduria'],
  ['DIAN', 'dian'],
];

const sourceKeyForFinding = (finding) => {
  const src = finding?.details?.source || '';
  for (const [label, key] of SOURCE_LABEL_TO_KEY) {
    if (src.includes(label)) return key;
  }
  return null;
};

const isRetryable = (finding) => {
  const code = finding?.details?.status_code;
  return code === 'PORTAL_NO_DISPONIBLE'
    || code === 'CAPTCHA_REQUERIDO'
    || code === 'ACCESO_BLOQUEADO'
    || code === 'CLOUDFLARE'
    || code === 'NO_IMPLEMENTADO';
};

const FINDING_TYPE_LABELS = {
  identity: 'Identidad',
  judicial: 'Antecedentes Judiciales / Disciplinarios',
  restrictive_lists: 'Listas Restrictivas Internacionales',
  financial: 'Situación Fiscal / Financiera',
  registry: 'Registros Oficiales',
  soat: 'SOAT',
  tecno: 'Tecnomecánica',
};

// Map backend name_source codes → user-facing labels
const NAME_SOURCE_LABELS = {
  procuraduria: 'Obtenido de Procuraduría',
  policia: 'Obtenido de Policía Nacional',
  rnec_registraduria: 'Obtenido de Registraduría',
  verifik_registraduria: 'Obtenido de Registraduría',
  user_input: 'Nombre ingresado por el usuario, no verificado contra el documento',
  not_available_ce: 'Nombre no disponible para cédula de extranjería',
  not_available: '',
};

const nameOriginLabel = (raw) => {
  if (!raw) return '';
  if (raw.name_origin_label) return raw.name_origin_label;
  if (!(raw.full_name || '').trim()) return '';
  return NAME_SOURCE_LABELS[raw.name_source] || '';
};

// Normalize datos so the Datos Consultados card always shows nice Spanish labels
// AND fills in the resolved name from Procuraduría / Policía / Registraduría
// when the user reached this page from the historial (where only request_data
// is available).
const buildDisplayDatos = (datos, tipo, result) => {
  if (!datos) return {};
  const raw = result?.raw_data || {};

  // If incoming `datos` already uses Spanish labels, keep it as-is.
  const keys = Object.keys(datos);
  const looksFormatted = keys.some((k) => k.includes(' ') || /[áéíóúÁÉÍÓÚñÑ]/.test(k));
  if (looksFormatted) return datos;

  // Otherwise it came straight from the historial (`request_data`). Map keys.
  if (tipo === 'person') {
    const docType = datos.document_type || raw.document_type || 'CC';
    const nameSource = raw.name_source || '';
    const noNameAvailable = nameSource === 'not_available_ce' || nameSource === 'not_available' || !(raw.full_name || `${datos.first_name || ''} ${datos.last_name || ''}`).trim();
    const first = !noNameAvailable ? (raw.first_name || datos.first_name || '') : '';
    const last = !noNameAvailable ? (raw.last_name || datos.last_name || '') : '';
    const fullName = !noNameAvailable
      ? (raw.full_name || `${first} ${last}`.trim() || '—')
      : '—';
    const fuenteLabel = nameOriginLabel(raw);
    return {
      'Tipo de Documento': docType || '—',
      'Número de Documento': datos.document_number || '—',
      'Nombres': first || '—',
      'Apellidos': last || '—',
      'Nombre Completo': fullName || '—',
      'Fuente del nombre': fuenteLabel || (noNameAvailable ? '—' : ''),
    };
  }
  if (tipo === 'company') {
    return {
      'NIT': datos.nit || datos.document_number || '—',
      'Razón Social': datos.company_name || raw.company_name || '—',
    };
  }
  if (tipo === 'vehicle') {
    return {
      'Placa': datos.plate || '—',
      'Tipo de Vehículo': datos.vehicle_type || '—',
    };
  }
  return datos;
};

const ReporteCompleto = () => {
  const location = useLocation();
  const { result, tipo, datos: rawDatos } = location.state || {};
  const reportRef = useRef();
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [liveResult, setLiveResult] = useState(result);
  const [findings, setFindings] = useState(result?.findings || []);
  const [retrying, setRetrying] = useState({}); // { [findingIdx]: true }
  const [retryingPending, setRetryingPending] = useState(false);
  const [completeFirst, setCompleteFirst] = useState('');
  const [completeLast, setCompleteLast] = useState('');
  const [completingName, setCompletingName] = useState(false);
  const [completeNameError, setCompleteNameError] = useState('');
  const findingsVisibles = findings.filter(f => f.status !== 'incomplete');
  const hasIncomplete = findings.some((f) => f.status === 'incomplete');
  const rawLive = liveResult?.raw_data || result?.raw_data || {};
  const personFullName = (rawLive.full_name || '').trim();
  const personOrigin = nameOriginLabel(rawLive);
  const needsName = tipo === 'person' && !personFullName;
  const baseDatos = buildDisplayDatos(rawDatos, tipo, liveResult || result);
  const datos = tipo === 'person'
    ? {
        ...baseDatos,
        Nombres: rawLive.first_name || baseDatos.Nombres || '—',
        Apellidos: rawLive.last_name || baseDatos.Apellidos || '—',
        'Nombre Completo': personFullName || '—',
        'Fuente del nombre': personOrigin || (needsName ? '—' : (baseDatos['Fuente del nombre'] || '')),
      }
    : baseDatos;

  const validationId = location.state?.validation_id || result?.validation_id;

  // Diagnóstico: log doc_type recibido y la fuente del nombre usada.
  // Útil para auditar el flujo anti-alucinación al consultar CE/CC.
  useEffect(() => {
    if (tipo === 'person' && result?.raw_data) {
      const rd = result.raw_data;
      // eslint-disable-next-line no-console
      console.log('[NIKA · ReporteCompleto] tipo_documento=', rd.document_type,
        '| name_source=', rd.name_source,
        '| nombre_final=', rd.full_name || '(vacío)');
    }
  }, [tipo, result]);

  const handleRetry = async (idx, finding) => {
    const sourceKey = sourceKeyForFinding(finding);
    if (!sourceKey || !validationId) return;
    setRetrying((s) => ({ ...s, [idx]: true }));
    try {
      const token = localStorage.getItem('nika-auth-token');
      const { data } = await axios.post(
        `${API}/api/validations/${validationId}/retry`,
        { source: sourceKey },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data?.finding) {
        setFindings((prev) => prev.map((f, i) => (i === idx ? data.finding : f)));
      }
    } catch (e) {
      console.error('Retry failed:', e);
    } finally {
      setRetrying((s) => {
        const n = { ...s }; delete n[idx]; return n;
      });
    }
  };

  const handleCompleteName = async (e) => {
    e.preventDefault();
    if (!validationId || completingName) return;
    setCompletingName(true);
    setCompleteNameError('');
    try {
      const token = localStorage.getItem('nika-auth-token');
      const { data } = await axios.post(
        `${API}/api/validations/${validationId}/complete-name`,
        { first_name: completeFirst, last_name: completeLast },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data?.result) {
        setLiveResult({ ...liveResult, ...data.result });
        setFindings(data.result.findings || []);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No se pudo completar el nombre. Intenta de nuevo.';
      setCompleteNameError(typeof msg === 'string' ? msg : 'No se pudo completar el nombre.');
    } finally {
      setCompletingName(false);
    }
  };

  const handleRetryPending = async () => {
    if (!validationId || retryingPending) return;
    setRetryingPending(true);
    try {
      const token = localStorage.getItem('nika-auth-token');
      const { data } = await axios.post(
        `${API}/api/validations/${validationId}/retry-pending`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data?.result) {
        setLiveResult({ ...liveResult, ...data.result });
        setFindings(data.result.findings || []);
      }
    } catch (e) {
      console.error('Retry pending failed:', e);
    } finally {
      setRetryingPending(false);
    }
  };

  if (!result || !tipo || !rawDatos) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Reporte no encontrado</h2>
          <Link to="/validaciones">
            <Button>Volver a Validaciones</Button>
          </Link>
        </div>
      </div>
    );
  }

  const getStatusIcon = (st) => {
    switch (st) {
      case 'verified': case 'clear': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'alert': return <XCircle className="h-5 w-5 text-red-600" />;
      case 'incomplete': return <Clock className="h-5 w-5 text-gray-400" />;
      default: return <Clock className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusBadgeClass = (st) => {
    if (st === 'verified' || st === 'clear') return 'bg-green-100 text-green-800';
    if (st === 'warning') return 'bg-yellow-100 text-yellow-800';
    if (st === 'alert') return 'bg-red-100 text-red-800 font-bold';
    if (st === 'incomplete') return 'bg-gray-100 text-gray-500';
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (st) => {
    const map = { verified: 'Verificado', clear: 'Sin Hallazgos', warning: 'Advertencia', alert: 'ALERTA', error: 'Error', incomplete: 'No consultado' };
    return map[st] || st;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 70) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getRiskLevelInfo = (level) => {
    const map = {
      low: { text: 'Riesgo Bajo', color: 'bg-green-100 text-green-800' },
      medium: { text: 'Riesgo Medio', color: 'bg-yellow-100 text-yellow-800' },
      high: { text: 'Riesgo Alto', color: 'bg-red-100 text-red-800' },
      critical: { text: 'Riesgo Crítico', color: 'bg-red-200 text-red-900 font-bold' },
    };
    return map[level] || { text: 'Sin Evaluar', color: 'bg-gray-100 text-gray-800' };
  };

  const getTipoInfo = (t) => {
    const map = {
      person: {
        title: 'Validación de Persona Natural',
        icon: <User className="h-6 w-6" />,
        subtitle: 'Verificación de identidad, antecedentes y listas restrictivas internacionales',
      },
      company: {
        title: 'Validación Empresarial',
        icon: <Building2 className="h-6 w-6" />,
        subtitle: 'Verificación de registro, estado tributario y listas restrictivas',
      },
      vehicle: {
        title: 'Validación Vehicular',
        icon: <Car className="h-6 w-6" />,
        subtitle: 'Verificación de registro vehicular y estado legal',
      },
    };
    return map[t] || { title: 'Validación', icon: <FileText className="h-6 w-6" />, subtitle: '' };
  };

  const handlePrint = () => window.print();

  const handleDownloadPDF = async () => {
    setGeneratingPdf(true);
    try {
      const validationId = result?.validation_id || result?.id || location.state?.validation_id;
      if (!validationId) {
        // Fallback: build a client-side HTML if we don't have a validation_id
        const blob = new Blob([generateReportHTML()], { type: 'text/html' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `reporte-nika-${tipo}-${Date.now()}.html`;
        document.body.appendChild(a); a.click(); a.remove();
        return;
      }
      const token = localStorage.getItem('nika-auth-token');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/validations/${validationId}/pdf`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `reporte-nika-${tipo}-${validationId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      console.error('PDF download error', e);
      alert('No se pudo generar el PDF. Intenta de nuevo.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const generateReportHTML = () => {
    const tipoInfo = getTipoInfo(tipo);
    const riskInfo = getRiskLevelInfo(result.risk_level);
    const sources = result.raw_data?.sources_consulted || [];
    return `<!DOCTYPE html>
<html lang="es">
<head><meta charset="UTF-8"><title>Reporte NIKA – ${tipoInfo.title}</title>
<style>
  body{font-family:Arial,sans-serif;margin:24px;color:#333;font-size:13px}
  h1{color:#1d4ed8;font-size:20px} h2{font-size:15px;margin-top:20px;border-bottom:1px solid #ddd;padding-bottom:4px}
  .header{text-align:center;border-bottom:2px solid #1d4ed8;padding-bottom:16px;margin-bottom:24px}
  .logo{font-size:32px;font-weight:bold;color:#1d4ed8;letter-spacing:4px}
  .score-box{display:inline-block;padding:8px 20px;border-radius:8px;font-size:22px;font-weight:bold;margin:8px 0}
  .finding{margin:10px 0;padding:12px;border-left:4px solid #ddd;background:#f9f9f9;border-radius:4px}
  .clear,.verified{border-left-color:#10b981} .warning{border-left-color:#f59e0b} .alert{border-left-color:#ef4444;background:#fff5f5}
  .badge{padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600}
  .badge-red{background:#fee2e2;color:#991b1b} .badge-green{background:#dcfce7;color:#166534} .badge-yellow{background:#fef9c3;color:#854d0e}
  table{width:100%;border-collapse:collapse;margin-top:8px} td,th{padding:6px 8px;border:1px solid #e5e7eb;font-size:12px} th{background:#f3f4f6}
  ul{margin:4px 0;padding-left:16px} li{font-size:11px;margin:2px 0}
  .footer{margin-top:32px;padding-top:16px;border-top:1px solid #ddd;font-size:11px;color:#666;text-align:center}
</style></head>
<body>
<div class="header">
  <div class="logo">NIKA</div>
  <h1>${tipoInfo.title}</h1>
  <p>Generado: ${new Date().toLocaleString('es-CO')} | Cumplimiento SARLAFT – Res. 4607 de 2026</p>
</div>
<div style="text-align:center;margin-bottom:16px">
  <div class="score-box" style="background:${result.score>=90?'#dcfce7':result.score>=70?'#fef3c7':'#fee2e2'};color:${result.score>=90?'#166534':result.score>=70?'#92400e':'#991b1b'}">
    Puntaje: ${result.score}/100
  </div>
  <br><span class="badge ${result.risk_level==='low'?'badge-green':result.risk_level==='medium'?'badge-yellow':'badge-red'}">${riskInfo.text}</span>
  ${result.raw_data?.consulta_parcial ? `<p style="color:#6b7280;margin-top:8px">${result.raw_data.consulta_parcial}</p>` : ''}
</div>
<h2>Datos Consultados</h2>
<table><tbody>${Object.entries(datos).map(([k,v])=>`<tr><td><b>${k}</b></td><td>${v}</td></tr>`).join('')}</tbody></table>
<h2>Hallazgos (${result.findings?.filter(f => f.status !== 'incomplete').length || 0})</h2>
${result.findings?.filter(f => f.status !== 'incomplete').map(f=>`
<div class="finding ${f.status}">
  <b>${FINDING_TYPE_LABELS[f.type]||f.type}</b>
  ${f.status==='alert'?'<span class="badge badge-red">ALERTA</span>':f.status==='clear'||f.status==='verified'?'<span class="badge badge-green">Sin novedad</span>':''}
  <p>${f.message}</p>
  ${f.details?.sources_checked?`<p><b>Listas consultadas:</b></p><ul>${f.details.sources_checked.map(s=>`<li>&#10003; ${s}</li>`).join('')}</ul>`:''}
  ${f.details?.matches?.length?`<p><b>Coincidencias:</b></p>${f.details.matches.map(m=>`<div style="background:#fff0f0;border:1px solid #fca5a5;padding:8px;margin:4px 0;border-radius:4px"><b>${m.name}</b> | ${m.source}${m.programs?.length?` | ${m.programs.join(', ')}`:''}</div>`).join('')}`:''}
  ${f.details?.manual_url?`<p>Consulta manual: <a href="${f.details.manual_url}">${f.details.manual_url}</a></p>`:''}
</div>`).join('')||'<p>Sin hallazgos.</p>'}
${sources.length?`<h2>Fuentes Consultadas</h2><ul>${sources.map(s=>`<li>&#10003; ${s}</li>`).join('')}</ul>`:''}
<div class="footer">
  Reporte NIKA – Plataforma de Validación de Datos | Documento confidencial para uso autorizado<br>
  Cumplimiento SARLAFT – Resolución 4607 de 2026 | &copy; ${new Date().getFullYear()} NIKA
</div>
</body></html>`;
  };

  const tipoInfo = getTipoInfo(tipo);
  const riskInfo = getRiskLevelInfo(liveResult.risk_level);

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white">
      {/* Top navigation bar */}
      <div className="print:hidden bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/validaciones" className="flex items-center space-x-2 text-blue-600 hover:text-blue-700">
            <ArrowLeft className="h-5 w-5" />
            <span>Volver a Validaciones</span>
          </Link>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={handlePrint} data-testid="btn-print">
              <Printer className="h-4 w-4 mr-2" />Imprimir
            </Button>
            <Button
              onClick={handleDownloadPDF}
              disabled={generatingPdf}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="btn-download-pdf"
            >
              <Download className="h-4 w-4 mr-2" />
              {generatingPdf ? 'Generando...' : 'Descargar Reporte'}
            </Button>
          </div>
        </div>
      </div>

      <div ref={reportRef} className="container mx-auto px-4 py-8 print:px-0 print:py-4">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-6 print:shadow-none">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-blue-700 tracking-widest mb-2">NIKA</h1>
            <div className="flex justify-center items-center space-x-2 mb-1">
              {tipoInfo.icon}
              <h2 className="text-xl font-bold text-gray-900">{tipoInfo.title}</h2>
            </div>
            <p className="text-gray-500 text-sm">{tipoInfo.subtitle}</p>
            {tipo === 'person' && (
              <div className="mt-4" data-testid="report-person-name-header">
                <p className="text-lg font-semibold text-gray-900">{personFullName || 'Nombre no disponible'}</p>
                {personOrigin ? (
                  <p className="text-sm text-gray-600 mt-1" data-testid="report-name-origin">{personOrigin}</p>
                ) : null}
              </div>
            )}
            <div className="flex justify-center items-center space-x-4 mt-4 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />{new Date().toLocaleDateString('es-CO')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />{new Date().toLocaleTimeString('es-CO')}
              </span>
            </div>
          </div>
          <div className="flex justify-center">
            <Card className={`w-fit ${getScoreColor(liveResult.score)} border-2`}>
              <CardContent className="p-6 text-center">
                <div className="text-5xl font-bold mb-1" data-testid="report-score">{liveResult.score}/100</div>
                <div className="text-xs font-medium mb-3 uppercase tracking-wide">Puntaje de Confianza SARLAFT</div>
                <Badge className={riskInfo.color} data-testid="report-risk-level">{riskInfo.text}</Badge>
              </CardContent>
            </Card>
          </div>
          {liveResult.raw_data?.consulta_parcial && (
            <p className="text-center text-sm text-gray-500 mt-4" data-testid="consulta-parcial">
              {liveResult.raw_data.consulta_parcial}
            </p>
          )}
          {tipo === 'person' && hasIncomplete && validationId && (
            <div className="flex justify-center mt-4 print:hidden">
              <Button
                variant="outline"
                onClick={handleRetryPending}
                disabled={retryingPending}
                data-testid="retry-pending-sources"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${retryingPending ? 'animate-spin' : ''}`} />
                {retryingPending ? 'Reintentando…' : 'Reintentar fuentes pendientes'}
              </Button>
            </div>
          )}
          {needsName && validationId && (
            <Card className="mt-6 border-amber-200 bg-amber-50 print:hidden" data-testid="complete-name-card">
              <CardHeader>
                <CardTitle className="text-base">Completar con nombre y apellido</CardTitle>
                <CardDescription>
                  El presupuesto de consulta se agotó sin obtener un nombre. Escríbelo para cribado inmediato en listas restrictivas.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCompleteName} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="complete-first-name">Nombres</Label>
                      <Input
                        id="complete-first-name"
                        value={completeFirst}
                        onChange={(e) => setCompleteFirst(e.target.value)}
                        required
                        data-testid="complete-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="complete-last-name">Apellidos</Label>
                      <Input
                        id="complete-last-name"
                        value={completeLast}
                        onChange={(e) => setCompleteLast(e.target.value)}
                        required
                        data-testid="complete-last-name"
                      />
                    </div>
                  </div>
                  {completeNameError ? (
                    <p className="text-sm text-red-700" data-testid="complete-name-error">{completeNameError}</p>
                  ) : null}
                  <Button type="submit" disabled={completingName} data-testid="complete-name-submit">
                    {completingName ? 'Consultando listas…' : 'Completar y consultar listas'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Queried data */}
        <Card className="mb-6 print:shadow-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5" />Datos Consultados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(datos).map(([key, value]) => (
                <div key={key} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-gray-500 text-sm font-medium">{key}:</span>
                  <span className="text-gray-900 text-sm font-semibold">{value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Findings */}
        <Card className="mb-6 print:shadow-none">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5" />Hallazgos Detallados
              </div>
              <Badge variant="outline" data-testid="findings-count">
                {findingsVisibles.length} hallazgo(s)
              </Badge>
            </CardTitle>
            <CardDescription>
              Resultados de consulta con fuentes oficiales y listas restrictivas internacionales (SARLAFT)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {findings?.length > 0 ? (
              <div className="space-y-4">
                {findingsVisibles.map((finding, idx) => {
                  return (
                  <div
                    key={idx}
                    data-testid={`finding-${idx}`}
                    className={`border rounded-lg p-4 transition-colors ${
                      finding.status === 'alert'
                        ? 'border-red-300 bg-red-50'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {getStatusIcon(finding.status)}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-gray-900 text-sm">
                            {FINDING_TYPE_LABELS[finding.type] || finding.type}
                          </h3>
                          <Badge className={getStatusBadgeClass(finding.status)}>
                            {getStatusLabel(finding.status)}
                          </Badge>
                        </div>
                        <p className="text-gray-700 text-sm mb-2">{finding.message}</p>

                        {/* Restrictive lists: show which lists were checked + matches */}
                        {finding.type === 'restrictive_lists' && finding.details && (
                          <div className="mt-2 space-y-2">
                            {finding.details.sources_checked?.length > 0 && (
                              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                                <div className="flex items-center gap-1 text-xs font-semibold text-blue-800 mb-2">
                                  <List className="h-3 w-3" />Listas consultadas en tiempo real:
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                                  {finding.details.sources_checked.map((s, i) => (
                                    <div key={i} className="flex items-center gap-1 text-xs text-blue-700">
                                      <CheckCircle className="h-3 w-3 text-blue-500 flex-shrink-0" />{s}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {finding.details.matches?.length > 0 && (
                              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                                <div className="flex items-center gap-1 text-xs font-semibold text-red-800 mb-2">
                                  <AlertCircle className="h-3 w-3" />Coincidencias encontradas ({finding.details.matches.length}):
                                </div>
                                {finding.details.matches.map((m, mi) => (
                                  <div key={mi} className="text-xs bg-white border border-red-100 rounded-lg p-3 mb-2">
                                    <div className="font-semibold text-red-900 mb-1">{m.name}</div>
                                    <div className="text-gray-600">
                                      <span className="font-medium">Fuente:</span> {m.source}
                                    </div>
                                    {m.entity_type && (
                                      <div className="text-gray-600">
                                        <span className="font-medium">Tipo:</span> {m.entity_type}
                                      </div>
                                    )}
                                    {m.programs?.length > 0 && (
                                      <div className="text-gray-600">
                                        <span className="font-medium">Programas:</span> {m.programs.join(', ')}
                                      </div>
                                    )}
                                    {m.doc_numbers?.length > 0 && (
                                      <div className="text-gray-600">
                                        <span className="font-medium">Documentos registrados:</span> {m.doc_numbers.join(', ')}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Other finding types: show details */}
                        {finding.type !== 'restrictive_lists' && finding.details && (
                          <div className="bg-gray-50 p-3 rounded-lg border mt-2">
                            <div className="text-xs text-gray-600 space-y-1">
                              {Object.entries(finding.details)
                                .filter(([k]) => !['matches', 'sources_checked'].includes(k))
                                .map(([k, v]) => (
                                  <div key={k} className="flex gap-2">
                                    <span className="font-medium capitalize min-w-[120px]">{k.replace(/_/g, ' ')}:</span>
                                    <span className="text-gray-700 break-all">
                                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Retry button for failed scrapers */}
                        {validationId && isRetryable(finding) && sourceKeyForFinding(finding) && (
                          <div className="mt-3 print:hidden">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRetry(idx, finding)}
                              disabled={!!retrying[idx]}
                              data-testid={`retry-finding-${idx}`}
                              className="text-xs"
                            >
                              {retrying[idx] ? (
                                <>
                                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                  Reintentando…
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="h-3 w-3 mr-1" />
                                  Reintentar consulta a {finding.details.source}
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Sin hallazgos registrados.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sources consulted */}
        {liveResult.raw_data?.sources_consulted && (
          <Card className="mb-6 print:shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <List className="h-4 w-4" />Fuentes Consultadas
              </CardTitle>
              {liveResult.raw_data.primary_identifier && (
                <CardDescription>
                  Identificador primario de consulta: <strong>{liveResult.raw_data.primary_identifier}</strong>
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {liveResult.raw_data.sources_consulted.map((s, i) => {
                  const unavailable = typeof s === 'string' && s.includes('No disponible al momento de la consulta');
                  const needsName = typeof s === 'string' && s.includes('Requiere nombre y apellido');
                  return (
                  <div key={i} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${unavailable || needsName ? 'text-gray-500 bg-gray-50' : 'text-gray-700 bg-gray-50'}`}>
                    {unavailable || needsName
                      ? <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      : <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />}
                    {s}
                  </div>
                  );
                })}
              </div>
              {liveResult.raw_data.validation_timestamp && (
                <div className="mt-3 text-xs text-gray-400">
                  Consulta realizada: {new Date(liveResult.raw_data.validation_timestamp).toLocaleString('es-CO')}
                  {liveResult.last_retry_at || liveResult.raw_data.last_retry_at
                    ? ` · Reintento: ${new Date(liveResult.last_retry_at || liveResult.raw_data.last_retry_at).toLocaleString('es-CO')}`
                    : ''}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm border p-6 print:shadow-none">
          <Separator className="mb-4" />
          <div className="text-center text-sm text-gray-600">
            <p className="mb-1 font-semibold">Reporte generado por NIKA – Plataforma de Validación de Datos</p>
            <p className="mb-1 text-xs text-gray-400">
              Este reporte es confidencial. Cumplimiento SARLAFT – Resolución 4607 de 2026.
            </p>
            <p className="text-xs text-gray-400">
              &copy; {new Date().getFullYear()} NIKA. Todos los derechos reservados. |{' '}
              {new Date().toLocaleString('es-CO')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReporteCompleto;
